import {
  Avatar,
  Box,
  Flex,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Spinner,
  Tag,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import clsx from "clsx";
import { isEmpty } from "lodash";
import { useContext, useState } from "react";
import { MdChevronRight, MdMoreVert } from "react-icons/md";
import {
  MdsEditOutlined,
  MdsRedoOutlined,
} from "react-icons-with-materialsymbols/mds";
import { useParams } from "react-router-dom";

import { useShowToast } from "@/components/toast";
import { getPreviewVersion } from "@/features/llm-prompt/redux";
import { useAppDispatch, useAppSelector } from "@/reduxHooks";
import { ModalTypes, openModal } from "@/slices/modal-slice";
import { formatTimestamp } from "@/utils/date-convertor";

import { workflowManagerApi } from "../../api";
import {
  useLazyGetWorkflowQuery,
  useRestoreWorkflowVersionMutation,
} from "../../api/workflow-api";
import { ReactFlowInstanceContext } from "../../components/flow-editor";
import { useSyncWorkflow } from "../../hooks/useSyncWorkflow";
import {
  currentPreviewVersion,
  setPreviewVersion,
  triggerVersionHistoryRefresh,
} from "../../redux/workflow-slice";
import { WorkflowVersionSchema } from "../../types/workflow-types";
import { createReactflowObjectFromWorkflow } from "../../utils";

const VersionHistoryItem = ({
  version,
  index,
  hasChildren,
  isChild,
  isOpen,
  onToggle,
  setNextUrl,
  scrollToTop,
}: {
  version: WorkflowVersionSchema;
  index: number;
  hasChildren?: boolean;
  isChild?: boolean;
  isOpen?: boolean;
  onToggle?: () => void;
  setNextUrl: (nextUrl: string | null) => void;
  scrollToTop?: () => void;
}) => {
  const dispatch = useAppDispatch();
  const params = useParams();
  const previewVersion = useAppSelector(currentPreviewVersion);
  const [isRestoring, setIsRestoring] = useState(false);
  const [getSpecificWorkflowVersion, { isFetching }] =
    useLazyGetWorkflowQuery();
  const { setNodesFn, setEdgesFn, editorInstance } = useContext(
    ReactFlowInstanceContext
  )!;
  const { refetchWorkflow } = useSyncWorkflow();
  const toast = useShowToast(undefined, undefined, true);
  const {
    isOpen: menuIsOpen,
    onOpen: menuOnOpen,
    onClose: menuOnClose,
  } = useDisclosure();

  const [restoreWorkflowVersion] = useRestoreWorkflowVersionMutation();

  const parseDescription = (description: string | null) => {
    if (!description) return formatTimestamp(version.createdOn);
    try {
      const parsed = JSON.parse(description);
      if (!parsed.message) return description;
      let message = parsed.message;
      if (Array.isArray(parsed.variables)) {
        parsed.variables.forEach(
          (variable: { name: string; value: string; type?: string }) => {
            let value = variable.value;
            if (variable.type === "timestamp") value = formatTimestamp(value);
            message = message.replace(`{${variable.name}}`, value);
          }
        );
      }
      return message;
    } catch {
      return description;
    }
  };

  const title = isEmpty(version.description)
    ? formatTimestamp(version.createdOn)
    : parseDescription(version.description);

  const handleVersionClick = async () => {
    if (!params.editorId) return;
    // Fetch the workflow version data
    const result = await getSpecificWorkflowVersion({
      workflowId: params.editorId!,
      wfRunId: version.id,
      isPreview: "true",
    });
    if (result.data) {
      // Set this version as preview version
      const workflowVersionResponse = result.data.response.data?.workflows[0];
      if (!workflowVersionResponse) return;
      const { configuredNodes, configuredEdges } =
        createReactflowObjectFromWorkflow([], workflowVersionResponse);
      dispatch(
        setPreviewVersion({
          isCurrentVersion: index === 0,
          versionTagId: version.id,
          versionMetaData: version,
          workflow: {
            nodes: configuredNodes,
            edges: configuredEdges,
            viewport: {
              x: 0,
              y: 0,
              zoom: 1,
            },
          },
        })
      );
    }
  };

  const handleRestoreVersion = async () => {
    if (!params.editorId) return;
    try {
      setIsRestoring(true);
      const result = await restoreWorkflowVersion({
        workflowId: params.editorId!,
        wfRunId: version.id,
      });

      dispatch(
        workflowManagerApi.util.updateQueryData(
          "getWorkflowVersions",
          { workflowId: params.editorId! },
          () => ({
            response: {
              data: {
                runs: [],
              },
              pagination: {
                next: null,
                count: 0,
                previous: null,
              },
            },
          })
        )
      );

      if (result.data) {
        await refetchWorkflow(setNodesFn, setEdgesFn, editorInstance);
        dispatch(triggerVersionHistoryRefresh());
        toast({
          title: "Version restored",
          description: `The ${title} version has been restored`,
          status: "success",
        });
      }
      menuOnClose();
    } catch (error) {
      toast({
        title: "Error restoring version",
        description: "Failed to restore version",
        status: "error",
      });
    } finally {
      setIsRestoring(false);
    }
  };

  const handleRenameVersion = () => {
    dispatch(
      openModal({
        modalType: ModalTypes.RENAME_VERSION,
        modalProps: {
          versionName: title,
          workflowId: params.editorId!,
          wfRunId: version.id,
        },
      })
    );
  };

  return (
    <Box
      className={clsx(
        "border-b font-medium border-gray-200 py-4 gap-4 cursor-pointer",
        isOpen || previewVersion?.versionTagId === version.id
          ? "bg-gray-100"
          : "bg-white hover:bg-gray-100"
      )}
      key={version.id}
    >
      <Flex className={clsx("gap-1", isChild && "ml-6")}>
        {!isChild && hasChildren && (
          <IconButton
            className="text-gray-400 cursor-pointer p-1 hover:bg-gray-100 !rounded-full"
            fontSize={18}
            transform={isOpen ? "rotate(90deg)" : "rotate(0deg)"}
            aria-label="Toggle version history item"
            colorScheme="dark"
            icon={
              isFetching ? (
                <Spinner color="gray.600" size="xs" />
              ) : (
                <MdChevronRight className="-mt-1" />
              )
            }
            onClick={(e) => {
              e.stopPropagation();
              onToggle?.();
            }}
            size={"xs"}
            variant={"ghost"}
          />
        )}
        {!hasChildren && (
          <Box className="px-4 h-10 w-6 pt-1 grid place-content-start justify-center">
            {isFetching && <Spinner color="gray.600" size="xs" />}
          </Box>
        )}
        <Box className="flex-1" onClick={handleVersionClick}>
          <Flex className="flex-wrap mb-1 gap-2">
            <Text className="text-sm break-all">{title}</Text>
            {index === 0 && (
              <Tag
                className="!py-1 shrink-0 !px-2 !rounded !my-0 !h-fit"
                colorScheme="green"
                size="sm"
              >
                Current Version
              </Tag>
            )}
          </Flex>
          {!isEmpty(version.description) && (
            <Text className="text-xs text-gray-500 mb-2">
              Created on {formatTimestamp(version.createdOn)}
            </Text>
          )}
          <Flex className="gap-2 text-sm items-center">
            <Avatar bg="yellow.200" name={version.createdByName} size="xs" />
            <Text>{version.createdByName}</Text>
          </Flex>
        </Box>
        <Menu
          closeOnBlur
          closeOnSelect={false}
          isOpen={menuIsOpen}
          onClose={menuOnClose}
        >
          <MenuButton
            as={IconButton}
            colorScheme="dark"
            icon={<MdMoreVert />}
            onClick={(e) => {
              e.stopPropagation();
              menuOnOpen();
            }}
            onContextMenu={(e) => e.stopPropagation()}
            variant="ghost"
          />
          <Portal>
            <MenuList
              className="mr-4 -mt-2 z-[999]"
              onContextMenu={(e) => e.stopPropagation()}
            >
              <MenuItem
                icon={<Icon as={MdsRedoOutlined} fontSize={20} />}
                isDisabled={isRestoring}
                onClick={handleRestoreVersion}
              >
                {isRestoring ? "Restoring..." : "Restore this version"}
              </MenuItem>
              <MenuItem
                icon={<Icon as={MdsEditOutlined} fontSize={20} />}
                onClick={handleRenameVersion}
              >
                Name this version
              </MenuItem>
            </MenuList>
          </Portal>
        </Menu>
      </Flex>
    </Box>
  );
};

export default VersionHistoryItem;
